<template>
  <div>
    <template v-if="menuItem">
      <v-list-item
        class="company-scope-menu-item"
        @click="handleMenuItemClick"
      >
        <v-list-item-icon class="mr-3">
          <v-icon size="18">
            {{ menuItemIcon }}
          </v-icon>
        </v-list-item-icon>

        <v-list-item-title class="avatar-menu-text">
          {{ resolvedMenuItemLabel }}
        </v-list-item-title>
      </v-list-item>
    </template>

    <template v-else>
      <universal-button
        class="company-scope-btn"
        :label="toggleButtonText"
        @click="openCompanyScopeDialog"
      />
    </template>

    <v-dialog
      v-model="companyScopeDialogOpen"
      content-class="elevation-0"
      style="z-index: 600;"
      max-width="674"
      :transition="false"
      @keydown.enter.prevent="saveCompanyScope"
      @keydown.esc.prevent="closeCompanyScopeDialog"
      @click:outside="closeCompanyScopeDialog"
    >
      <v-card
        class="v-card"
        :disabled="showLoader"
        @click:outside="closeCompanyScopeDialog"
      >
        <!--      TITLE-->
        <v-card-title
          class="v-card-title"
        >
          {{ computedDialog.title }}
          <v-spacer />

          <div
            class="x-image"
          >
            <v-img
              src="/img/icons/x-icon-image.svg"
              contain
              width="30"
              height="30"
              @click="closeCompanyScopeDialog"
            />
          </div>
        </v-card-title>

        <v-card-text
          class="dialog-message"
          style="overflow-y: auto; flex-grow: 1; padding-top: 20px;"
        >
          <loader-component
            v-if="showLoader"
            show
          />

          <autocomplete-field
            v-model="companyScopeId"
            :config="companiesAutocompleteField"
            :options="companiesAutocompleteField.autocomplete_options"
            :validation-errors="validationErrors"
            clearable
            @clear-validation-errors="clearInputValidationErrors"
          />
        </v-card-text>

        <v-card-actions>
          <v-row
            style="margin: 0;"
          >
            <v-col>
              <universal-button
                type="cancel"
                :label="$t('base.cancel')"
                class="universal-buttons"
                @click="closeCompanyScopeDialog"
              />
            </v-col>
            <v-col>
              <universal-button
                type="base"
                :label="$t('base.save')"
                class="universal-buttons"
                color="#65AD45"
                style="color: white !important;"
                @click="saveCompanyScope"
              />
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { api } from '@/global/services/api'
import { createNamespacedHelpers } from 'vuex'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import AutocompleteField from '@/global/components/fields/AutocompleteField.vue'
import LoaderComponent from '@/global/components/loader/Loader.vue'
const { mapActions } = createNamespacedHelpers('base/config')

const REFRESH_AFTER = 500
export default {
  name: 'CompanyScope',

  components: {
    LoaderComponent,
    AutocompleteField,
    UniversalButton
  },

  props: {
    companyScope: {
      type: Object,
      default: () => ({})
    },

    menuItem: {
      type: Boolean,
      default: false
    },

    menuItemLabel: {
      type: String,
      default: ''
    },

    menuItemIcon: {
      type: String,
      default: 'mdi-domain'
    }
  },

  data () {
    return {
      companyScopeId: null,
      companyScopeDialogOpen: false,
      validationErrors: {},
      openMenuAutomatically: false,
      showLoader: false
    }
  },

  computed: {
    computedCompanyScope () {
      return this.companyScope
    },

    computedConfig () {
      return this.computedCompanyScope?.config || {}
    },

    computedAutocompleteOptions () {
      return this.computedConfig?.autocomplete_options
    },

    computedAutocompleteLabel () {
      return this.computedConfig?.label
    },

    computedCompany () {
      return this.computedCompanyScope?.company
    },

    computedCompanyName () {
      return this.computedCompany?.name
    },

    computedDialog () {
      return this.computedCompanyScope?.dialog || {}
    },

    companiesAutocompleteField () {
      return {
        key: 'companyScopeId',
        label: this.computedAutocompleteLabel,
        autofocus: true,
        openMenuAutomatically: this.openMenuAutomatically,
        autocomplete_options: {
          ...this.computedAutocompleteOptions,
          queryParams: (event) => {
            const { isUserClick } = event || {}
            if (isUserClick) {
              return {
                limitResults: false
              }
            }
            return {}
          }
        }
      }
    },

    toggleButtonText () {
      return this.computedCompanyName || this.computedDialog.button_toggle
    },

    resolvedMenuItemLabel () {
      return this.menuItemLabel || this.toggleButtonText
    }
  },

  watch: {
    companyScope: {
      deep: true,
      immediate: true,
      handler (value) {
        if (value.company?.id) {
          this.companyScopeId = value.company.id
        }
        else {
          this.companyScopeId = null
        }
      }
    }
  },

  methods: {
    ...mapActions(['updateCompanyScope']),

    openCompanyScopeDialog () {
      this.companyScopeDialogOpen = true
      this.openMenuAutomatically = true
    },

    handleMenuItemClick () {
      this.$emit('request-open')
    },

    closeCompanyScopeDialog () {
      this.companyScopeDialogOpen = false
      this.openMenuAutomatically = false
    },

    async saveCompanyScope () {
      this.validationErrors = {}

      try {
        const data = {
          secondary_company_id: this.companyScopeId
        }

        this.showLoader = true
        await api('base').patch('company-scope', data)
          .then((data) => {
            // refresh current page after company change
            setTimeout(() => {
              window.location.reload()
            }, REFRESH_AFTER)
          })
      }
      catch (e) {
        this.validationErrors = e.getValidationErrors()
        this.showLoader = false
      }
    },

    clearInputValidationErrors (key) {
      if (this.validationErrors[key]) {
        this.validationErrors[key] = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
.company-scope-btn {
  border: 1px solid #374151 !important;
  background-color: #1F2937 !important;
  color: #FFFFFF;
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 20px !important;
}

.x-image:hover {
  cursor: pointer;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 17px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
  letter-spacing: 0 !important;
}

.dialog-message {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 32px;
  color: #111827 !important;
}

.v-card {
  max-width: 674px;
}

.universal-buttons {
  width: 100%;
  height: 40px !important;
}

::v-deep {
  .v-dialog {
    border-radius: 16px;
  }
}
</style>
